/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type Registry as Services, service } from '@ember/service';
import { action } from '@ember/object';
import { waitFor } from '@ember/test-waiters';
import { BalanceAdjustmentType } from 'uplisting-frontend/models/balance-adjustment';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import { isSaveDisabled } from 'uplisting-frontend/utils/changeset';

interface IArgs {
  booking: ModelRegistry['booking'];
}

export interface BookingPriceBalanceAdjustmentSignature {
  Element: HTMLTableCellElement;

  Args: IArgs;
}

export default class UiBookingPriceBalanceAdjustmentComponent extends Component<BookingPriceBalanceAdjustmentSignature> {
  @service notifications!: Services['notifications'];

  @service('repositories/balance-adjustment')
  balanceAdjustmentRepository!: Services['repositories/balance-adjustment'];

  isSaving = false;

  @tracked showAdjustModal = false;
  @tracked changeset!: GenericChangeset<ModelRegistry['balance-adjustment']>;

  types = Object.values(BalanceAdjustmentType);

  @action
  handleShowAdjustModal(): void {
    const { balanceAdjustmentRepository } = this;

    const record = balanceAdjustmentRepository.createRecord({
      ...balanceAdjustmentRepository.defaultParams,
      booking: this.args.booking,
    });

    this.changeset = balanceAdjustmentRepository.buildChangeset(record);

    this.showAdjustModal = true;
  }

  @action
  handleClose(): void {
    if (!this.isSaving) {
      this.changeset?.data.unloadRecord();
    }
  }

  @action
  @waitFor
  async handleSubmit(): Promise<void> {
    this.isSaving = true;

    const saveDisabled = await isSaveDisabled(this.changeset);

    if (saveDisabled) {
      this.isSaving = false;

      return;
    }

    try {
      await this.changeset.save();

      this.notifications.info(
        'action_bookings_price.balance_adjustment.notifications.saved',
      );

      await this.args.booking.reload();
    } catch {
      this.notifications.error();
    } finally {
      this.isSaving = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::Price::BalanceAdjustment': typeof UiBookingPriceBalanceAdjustmentComponent;
  }
}
